import { authRequest } from '../../../core/api/authApiClient';
import type { SettlementRepository } from '../Domain/repository/SettlementRepository';
import type { 
  SearchSettlementsRequest, 
  SearchSettlementsResponse,
  CommissionSumRequest,
  CommissionSumResponse,
  CommissionSearchRequest,
  CommissionSearchResponse,
  SettlementSearchParam
} from '../Domain/entity/SettlementTypes';

type TransactionSearchRequest = {
  params: SettlementSearchParam[];
  sort?: string;
  limit?: number;
  offset?: number;
};

type TransactionSearchItem = {
  transaction_id?: string;
  email?: string;
  comment?: string;
  sponsor_start_month?: string | number;
  sponsor_start_day?: string | number;
  sponsor_start_year?: string | number;
};

type TransactionSearchResponse = {
  statusCode?: number;
  result_message?: string;
  data?: TransactionSearchItem[];
};

const mapCommissionParamsToTransactionParams = (params: SettlementSearchParam[]) =>
  params.map((param) =>
    param.fld === 'pay_date'
      ? { ...param, fld: 'createdAt' }
      : param
  );

const normalizeDatePart = (value?: string | number) => {
  if (value === undefined || value === null) return '';
  const stringValue = String(value).trim();
  if (!stringValue) return '';
  return stringValue.padStart(2, '0');
};

const buildSponsorStartDate = (item: TransactionSearchItem) => {
  const year = String(item.sponsor_start_year ?? '').trim();
  const month = normalizeDatePart(item.sponsor_start_month);
  const day = normalizeDatePart(item.sponsor_start_day);
  if (!year || !month || !day) return undefined;
  return `${year}-${month}-${day}`;
};

/**
 * Settlement Repository 구현체
 */
export class SettlementRepositoryImpl implements SettlementRepository {
  async searchSettlements(request: SearchSettlementsRequest): Promise<SearchSettlementsResponse> {
    // POST /settlement/m/sch 엔드포인트 호출
    const res = await authRequest<any>('/settlement/m/sch', {
      method: 'POST',
      body: request,
    });

    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      throw new Error(String(res.result_message || '정산 검색 실패'));
    }
    return res as SearchSettlementsResponse;
  }
  
  async getCommissionSum(request: CommissionSumRequest): Promise<CommissionSumResponse> {
    // POST /mgmtcommission/m/sum 엔드포인트 호출
    const res = await authRequest<any>('/mgmtcommission/m/sum', {
      method: 'POST',
      body: request,
    });

    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      throw new Error(String(res.result_message || '수수료 합계 조회 실패'));
    }
    return res as CommissionSumResponse;
  }
  
  async searchCommissions(request: CommissionSearchRequest): Promise<CommissionSearchResponse> {
    // POST /mgmtcommission/m/sch 엔드포인트 호출
    const commissionRes = await authRequest<any>('/mgmtcommission/m/sch', {
      method: 'POST',
      body: request,
    });

    if (commissionRes.statusCode !== undefined && commissionRes.statusCode !== 200 && commissionRes.statusCode !== 0) {
      throw new Error(String(commissionRes.result_message || '수수료 검색 실패'));
    }

    const baseResponse = commissionRes as CommissionSearchResponse;
    if (!baseResponse.data?.length) {
      return baseResponse;
    }

    // 수수료 조회와 함께 transactions 조회를 추가해 email/comment/시작일 정보를 보강한다.
    try {
      const transactionLimit = Math.max(request.limit ?? 0, baseResponse.data.length, 100);
      const transactionRequest: TransactionSearchRequest = {
        params: mapCommissionParamsToTransactionParams(request.params),
        sort: (request.sort ?? 'createdAt:desc').replace(/created_at/g, 'createdAt'),
        limit: transactionLimit,
        offset: request.offset ?? 0,
      };

      const transactionRes = await authRequest<TransactionSearchResponse>('/transactionsall/m/sch', {
        method: 'POST',
        body: transactionRequest,
      });

      if (
        transactionRes.statusCode !== undefined &&
        transactionRes.statusCode !== 200 &&
        transactionRes.statusCode !== 0
      ) {
        throw new Error(String(transactionRes.result_message || '트랜잭션 검색 실패'));
      }

      const transactionMap = new Map<string, TransactionSearchItem>();
      (transactionRes.data ?? []).forEach((item) => {
        if (!item.transaction_id) return;
        transactionMap.set(String(item.transaction_id), item);
      });

      const commonData = baseResponse.data
        .filter((item) => transactionMap.has(item.transaction_id))
        .map((item) => {
          const transaction = transactionMap.get(item.transaction_id)!;
          return {
            ...item,
            email: transaction.email ?? item.email ?? '',
            comment: transaction.comment ?? item.comment ?? '',
            sponsor_start_month: transaction.sponsor_start_month ?? item.sponsor_start_month,
            sponsor_start_day: transaction.sponsor_start_day ?? item.sponsor_start_day,
            sponsor_start_year: transaction.sponsor_start_year ?? item.sponsor_start_year,
            sponsor_start_date: buildSponsorStartDate(transaction) ?? item.sponsor_start_date,
          };
        });

      return {
        ...baseResponse,
        data: commonData,
      };
    } catch {
      return {
        ...baseResponse,
        data: [],
      };
    }
  }
}

